import { ReactElement } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import { generateFrequencyString } from "byzantine";
import {
  getUntilEndString,
  getTotalAmount,
  DisplayStatus,
  getOriginalPaymentDirection,
  getPaymentFailureReason,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import Filters from "byzantine/src/filters";
import { InvoiceDownloadLink } from "../../common";
import { FILTER_CONFIG, FilterChips } from "../../Filters/filterConfig";
import styles from "../../PaymentActivity.module.scss";
import { PAYMENT_ACTIVITY_ROUTE } from "../../routes";

type ScheduleDetailsProps = {
  paymentActivity: API.FormattedPaymentActivity;
  rejectReasonCode?: API.FedNowTransaction["reject_reason_code"];
};

const ScheduleDetails = ({
  paymentActivity,
  rejectReasonCode,
}: ScheduleDetailsProps): ReactElement | null => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    displayStatus,
    recurrence,
    scheduledDate,
    frequency,
    rruleModel,
    amountDollars,
    parent,
    originalPayment,
    reversal,
    recipient,
    uuid,
    startDate,
    isReversal,
    invoice,
    isInternationalWirePayment,
  } = paymentActivity;

  const isCompleted = displayStatus === DisplayStatus.completed;
  const isInProgress = displayStatus === DisplayStatus.in_progress;
  const isCanceled = displayStatus === DisplayStatus.canceled;
  const isPendingApproval = displayStatus === DisplayStatus.pending_approval;

  const oneTimeScheduled = recurrence === 1;
  const parentPayment = !parent && rruleModel && !oneTimeScheduled;

  const renderReversedPaymentLink = () => {
    if (isReversal || !reversal) return null;
    const recipientName =
      paymentActivity?.destinationDetails?.beneficiary_name ||
      recipient?.name ||
      "";
    const reversalLabel = l10n.getString("approval-drawer-reversal-title", {
      transactionType:
        getOriginalPaymentDirection(paymentActivity) === "collect"
          ? "debit"
          : "credit",
      recipientName,
    });

    return (
      <div className="margin--top--m">
        <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
          {l10n.getString("label-reversed-payment")}
        </h5>
        <div>
          <Button
            kind="plain"
            size="s"
            onClick={() =>
              navigate(
                `/${PAYMENT_ACTIVITY_ROUTE}/${reversal}${location.search}`
              )
            }
          >
            {reversalLabel}
            <span className="narmi-icon-chevron-right" />
          </Button>
        </div>
      </div>
    );
  };

  const renderInvoiceField = () => {
    if (!invoice) {
      return null;
    }

    return (
      <div className="margin--top--m">
        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("invoice-review-invoice-label")}
        </h5>
        <InvoiceDownloadLink invoice={invoice} />
      </div>
    );
  };

  if (isReversal) {
    const recipientName =
      paymentActivity?.destinationDetails?.beneficiary_name ||
      recipient?.name ||
      "";
    const originalPaymentLabel =
      getOriginalPaymentDirection(paymentActivity) === "collect"
        ? l10n.getString("single-ach-payment-recipient-collect-from")
        : l10n.getString("single-ach-payment-recipient-send-to");

    return (
      <>
        {scheduledDate ? (
          <div className="margin--bottom--m">
            <h5 className={styles.drawerLabelHeading}>
              {isCanceled
                ? l10n.getString("label-canceled-date")
                : l10n.getString("label-scheduled-date")}
            </h5>
            <div>{Filters.longMonthDayYear(scheduledDate)}</div>
            {renderInvoiceField()}
          </div>
        ) : null}
        {originalPayment ? (
          <>
            <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
              {l10n.getString("label-original-payment")}
            </h5>
            <div>
              <Button
                kind="plain"
                size="s"
                onClick={() =>
                  navigate(
                    `/${PAYMENT_ACTIVITY_ROUTE}/${originalPayment}${location.search}`
                  )
                }
                testId="view-original-payment-button"
              >
                {`${originalPaymentLabel} ${recipientName}`}
                <span className="narmi-icon-chevron-right" />
              </Button>
            </div>
          </>
        ) : null}
      </>
    );
  }

  // recurring "parent" payment
  if (parentPayment) {
    const { until } = rruleModel.options;

    const showCount = recurrence && recurrence < 100;
    let endString = l10n.getString("approval-drawer-value-indefinite");

    if (until) {
      endString = getUntilEndString(rruleModel);
    } else if (showCount) {
      endString = l10n.getString("approval-drawer-value-ends-count", {
        count: recurrence,
      });
    }

    const totalAmount = showCount
      ? getTotalAmount(recurrence, amountDollars)
      : "";

    const handleClickViewRecurring = () => {
      navigate({
        pathname: `/${PAYMENT_ACTIVITY_ROUTE}`,
        search: `?${
          FILTER_CONFIG[FilterChips.RECURRING_PAYMENT].queryKey
        }=${uuid}`,
      });
    };

    return (
      <>
        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("approval-drawer-label-payment-schedule")}
        </h5>
        {frequency && startDate ? (
          <div className="margin--bottom--m">
            {l10n.getString("approval-drawer-value-payment-schedule", {
              frequency,
              startDate: Filters.longMonthDayYear(startDate.toISOString()),
            })}
          </div>
        ) : null}
        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("approval-drawer-label-schedule-ends")}
        </h5>
        <div className={cc({ "margin--bottom--m": totalAmount })}>
          {endString}
        </div>
        {isInternationalWirePayment && isCanceled && scheduledDate ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-canceled-date")}
            </h5>
            <div className="margin--bottom--m">
              {Filters.longMonthDayYear(scheduledDate)}
            </div>
          </>
        ) : null}
        {showCount ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("approval-drawer-label-payment-count", {
                count: recurrence,
              })}
            </h5>
            <div>{totalAmount}</div>
          </>
        ) : null}
        {isInProgress || isCompleted || isCanceled ? (
          <div>
            <Button
              kind="plain"
              size="s"
              onClick={handleClickViewRecurring}
              testId="view-recurring-payments-button"
            >
              {l10n.getString("label-view-recurring")}
              <span className="narmi-icon-chevron-right" />
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  // any 1 immediate payment that is pending approval
  if (isPendingApproval && !oneTimeScheduled) {
    return (
      <>
        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("label-scheduled-date")}
        </h5>
        <div>{l10n.getString("label-processes-approval")}</div>
        {renderInvoiceField()}
      </>
    );
  }

  // child payment
  if (parent && !oneTimeScheduled) {
    const localizedFrequencies = {
      ONCE: l10n.getString("frequency-once"),
      DAILY: l10n.getString("frequency-daily"),
      WEEKLY: l10n.getString("frequency-weekly"),
      EVERY_TWO_WEEKS: l10n.getString("frequency-biweekly"),
      FIRST_AND_FIFTEENTH: l10n.getString("frequency-first-and-fifteenth"),
      MONTHLY: l10n.getString("frequency-monthly"),
      YEARLY: l10n.getString("frequency-yearly"),
    };
    const parentFrequency = generateFrequencyString(
      rruleModel,
      localizedFrequencies
    );
    const formattedStartDate = startDate
      ? Filters.shortMonthDayYear(startDate.toISOString())
      : "";

    const handleClickParent = () => {
      navigate(`/${PAYMENT_ACTIVITY_ROUTE}/${parent}`);
    };

    return (
      <>
        {scheduledDate ? (
          <div className="margin--bottom--m">
            <h5 className={styles.drawerLabelHeading}>
              {isCanceled
                ? l10n.getString("label-canceled-date")
                : l10n.getString("label-scheduled-date")}
            </h5>
            <div>{Filters.longMonthDayYear(scheduledDate)}</div>
            {renderInvoiceField()}
          </div>
        ) : null}
        <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
          {l10n.getString("label-part-recurring")}
        </h5>
        <div>
          <Button
            kind="plain"
            size="s"
            onClick={handleClickParent}
            testId="view-parent-recurring-payment-button"
          >
            {`${parentFrequency} payment starting on ${formattedStartDate}`}
            <span className="narmi-icon-chevron-right" />
          </Button>
        </div>
        {renderReversedPaymentLink()}
      </>
    );
  }

  // immediate payment or one time scheduled
  return (
    <>
      {scheduledDate ? (
        <>
          <h5 className={styles.drawerLabelHeading}>
            {isCanceled
              ? l10n.getString("label-canceled-date")
              : l10n.getString("label-scheduled-date")}
          </h5>
          <div>{Filters.longMonthDayYear(scheduledDate)}</div>
          {renderInvoiceField()}
        </>
      ) : null}
      {rejectReasonCode ? (
        <div className="margin--top--m">
          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("label-failure-reason")}
          </h5>
          <div>{getPaymentFailureReason(rejectReasonCode, l10n)}</div>
        </div>
      ) : null}
      {renderReversedPaymentLink()}
    </>
  );
};

export default ScheduleDetails;
